import { reactive } from 'vue'
import { form_api_submit } from '@/utils/FormSubmit'
import { echo } from '@/echo.js'

export const chatState = reactive({
  subscribedConversationIds: new Set(),
  unreadCount: 0,
  activeConversationId: null,
  unreadByConversation: {},      // NEW — conversationId -> count
  latestByConversation: {},      // NEW — conversationId -> { body, attachment_name, sender_supplier_id, created_at }
})

export const POLL_INTERVAL_MS = 5 * 1000

export function subscribeToConversation(conversationId, mySupplierId, onMessage) {
  if (chatState.subscribedConversationIds.has(conversationId)) {
    console.log('[chatStore] already subscribed, skipping', conversationId)
    return
  }

  chatState.subscribedConversationIds.add(conversationId)
  console.log('[chatStore] subscribing to echo channel', `supplier-chat.${conversationId}`)

  echo.private(`supplier-chat.${conversationId}`)
    .listen('.SupplierMessageSent', (eventMessage) => {
      console.log('[chatStore] echo push received', conversationId, eventMessage)
      const isMine = Number(eventMessage.sender_supplier_id) === Number(mySupplierId)

      if (!isMine) {
        chatState.unreadCount++
        bumpConversationUnread(conversationId, eventMessage)
        console.log('[chatStore] unreadCount incremented (echo) ->', chatState.unreadCount)
      }

      onMessage(eventMessage, isMine)
    })
}

export function unsubscribeFromAll() {
  console.log('[chatStore] unsubscribing from all channels', [...chatState.subscribedConversationIds])
  chatState.subscribedConversationIds.forEach((conversationId) => {
    echo.leave(`supplier-chat.${conversationId}`)
  })
  chatState.subscribedConversationIds.clear()
}

export function clearUnread(amount) {
  console.log('[chatStore] clearUnread', amount, 'before:', chatState.unreadCount)
  chatState.unreadCount = Math.max(0, chatState.unreadCount - amount)
  console.log('[chatStore] clearUnread after:', chatState.unreadCount)
}

// NEW — per-conversation unread tracking, read by the sidebar to show "New message"
export function bumpConversationUnread(conversationId, latestMessage) {
  chatState.unreadByConversation[conversationId] = (chatState.unreadByConversation[conversationId] || 0) + 1
  chatState.latestByConversation[conversationId] = {
    body: latestMessage.body,
    attachment_name: latestMessage.attachment_name,
    sender_supplier_id: latestMessage.sender_supplier_id,
    created_at: latestMessage.created_at || new Date().toISOString(),
  }
}

export function clearConversationUnread(conversationId) {
  if (chatState.unreadByConversation[conversationId]) {
    delete chatState.unreadByConversation[conversationId]
  }
}

const lastMessageIdByConversation = {}
let pollTimer = null

export function setLastMessageId(conversationId, messageId) {
  if (!conversationId || !messageId) return
  const current = lastMessageIdByConversation[conversationId] || 0
  if (messageId > current) {
    lastMessageIdByConversation[conversationId] = messageId
    console.log('[chatStore] setLastMessageId', conversationId, '->', messageId)
  }
}

export function getLastMessageId(conversationId) {
  return lastMessageIdByConversation[conversationId] || 0
}

export async function pollConversation(conversationId) {
  const lastId = lastMessageIdByConversation[conversationId]
  console.log('[chatStore] pollConversation called for', conversationId, 'lastId:', lastId)

  if (!lastId) {
    console.warn('[chatStore] no baseline last_message_id set yet for', conversationId, '— skipping poll')
    return []
  }

  const result = await form_api_submit({
    url: `chat/${conversationId}/new-messages`,
    method: 'GET',
    data: { last_message_id: lastId },
    show_success_toast: false,
    show_error_toast: false,
  })

  console.log('[chatStore] pollConversation result for', conversationId, result)

  if (!result.success || !Array.isArray(result.data)) {
    console.warn('[chatStore] pollConversation failed or bad shape', result)
    return []
  }

  if (result.data.length) {
    console.log('[chatStore] pollConversation found', result.data.length, 'new message(s)')
    setLastMessageId(conversationId, result.data[result.data.length - 1].id)
  } else {
    console.log('[chatStore] pollConversation: no new messages')
  }

  return result.data
}

export function startGlobalPolling(mySupplierId, getActiveConversationId, onConversationsRefreshed) {
  stopGlobalPolling()
  console.log('[chatStore] startGlobalPolling for supplier', mySupplierId, 'every', POLL_INTERVAL_MS, 'ms')

  pollTimer = setInterval(async () => {
    console.log('[chatStore] global poll tick')

    const result = await form_api_submit({
      url: 'chat/conversations',
      method: 'GET',
      data: { sender_id: mySupplierId },
      show_success_toast: false,
      show_error_toast: false,
    })

    const rows = Array.isArray(result.data)
      ? result.data
      : Array.isArray(result.data?.data)
        ? result.data.data
        : []

    if (!result.success) return

    // NEW — hand the full, fresh conversation list back to the page so it can
    // merge in any brand-new conversations and subscribe to them via Echo.
    if (typeof onConversationsRefreshed === 'function') {
      onConversationsRefreshed(rows)
    }

    const activeId = getActiveConversationId ? getActiveConversationId() : null

    for (const conv of rows) {
      if (conv.id === activeId) {
        console.log('[chatStore] global poll: skipping active conversation entirely (page owns it)', conv.id)
        continue
      }

      if (!lastMessageIdByConversation[conv.id] && conv.last_message_id) {
        setLastMessageId(conv.id, conv.last_message_id)
        continue
      }

      const newMessages = await pollConversation(conv.id)
      if (!newMessages.length) continue

      const fromOthers = newMessages.filter(
        (m) => Number(m.sender_supplier_id) !== Number(mySupplierId)
      )

      if (fromOthers.length) {
        chatState.unreadCount += fromOthers.length
        console.log('[chatStore] global poll: unreadCount +=', fromOthers.length, '-> total:', chatState.unreadCount)

        const lastFromOther = fromOthers[fromOthers.length - 1]
        chatState.unreadByConversation[conv.id] = (chatState.unreadByConversation[conv.id] || 0) + fromOthers.length
        chatState.latestByConversation[conv.id] = {
          body: lastFromOther.body,
          attachment_name: lastFromOther.attachment_name,
          sender_supplier_id: lastFromOther.sender_supplier_id,
          created_at: lastFromOther.created_at,
        }
      }
    }
  }, POLL_INTERVAL_MS)
}

export function stopGlobalPolling() {
  if (pollTimer) {
    console.log('[chatStore] stopGlobalPolling')
    clearInterval(pollTimer)
    pollTimer = null
  }
}